import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  WritableSignal,
  contentChildren,
  forwardRef,
  input,
  signal
} from '@angular/core';
import { RadioButtonComponent } from './radio-button.component';
import { RADIO_GROUP_HOST } from '../inputs.constants';
import { InputColor, InputSize, RadioSelection } from '../inputs.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl
} from '@angular/forms';

@Component({
  selector: 'radio-group',
  standalone: true,
  host: RADIO_GROUP_HOST,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => RadioGroupComponent)
    }
  ],
  exportAs: 'radioGroup',
  template: `<div class="radio-group">
    <ng-content select="radio"></ng-content>
  </div>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RadioGroupComponent
  implements AfterContentInit, OnChanges, ControlValueAccessor
{
  @Output() selectionChange = new EventEmitter<RadioSelection | null>();

  uniqueGroupName = input.required<string>();
  color = input<InputColor>('secondary');
  size = input<InputSize>('md');
  radios = contentChildren<RadioButtonComponent>(
    forwardRef(() => RadioButtonComponent),
    { descendants: true }
  );

  groupName = signal('');
  selectedRadio: WritableSignal<RadioSelection | null> = signal(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private get _control(): AbstractControl<any, any> | null | undefined {
    const ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
      self: true
    });
    return ngControl?.control;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void = () => {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTouched: () => any = () => {};

  constructor(private _injector: Injector) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('color' in changes && this.radios().length)
      this.radios().forEach(radio => radio.color.set(this.color()));

    if ('size' in changes && this.radios().length)
      this.radios().forEach(radio => radio.size.set(this.size()));

    if ('uniqueGroupName' in changes) {
      if (
        !this.uniqueGroupName().length ||
        this.uniqueGroupName().includes(' ')
      ) {
        throw new Error('uniqueGroupName must not be empty or contain spaces.');
      } else {
        this.groupName.set(this.uniqueGroupName());
      }
    }
  }

  ngAfterContentInit(): void {
    if (!this.groupName()) return;
    if (this.radios().length) {
      this.radios().forEach(radio => {
        radio.inputName.set(this.groupName());
        radio.selectionChange
          .pipe(
            takeUntilDestroyed(radio.destroyRef),
            tap({
              next: radioSelection => {
                this.selectedRadio.set(radioSelection);
                this.radios().forEach(item => {
                  item.isChecked.set(item.id() === this.selectedRadio()?.id);
                  item.detectChange();
                });
                this._emitSelection();
                this.onChange(this.selectedRadio()?.value);
              }
            })
          )
          .subscribe();
        radio.focusChange
          .pipe(
            takeUntilDestroyed(radio.destroyRef),
            tap({
              next: () => {
                this.onTouched();
              }
            })
          )
          .subscribe();
      });
      this._checkTheRightRadio();
    }
  }

  writeValue(value: unknown): void {
    const radioToSelect = this.radios().find(radio => radio.value() === value);

    if (radioToSelect) {
      this.selectedRadio.set({
        id: radioToSelect.id(),
        value: radioToSelect.value()
      });
    } else {
      this.selectedRadio.set(null);
    }
    this._checkTheRightRadio();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.radios().length) {
      this.radios().forEach(radio => radio.isDisabled.set(isDisabled));
    }
  }

  private _checkTheRightRadio(): void {
    const selectedRadios = this.radios().filter(radio => radio.isChecked());

    if (this._control) {
      this._unCheckAllRadios(selectedRadios);
      const radioToSelect = this.radios().find(
        radio => radio.value() === this._control?.value
      );

      if (radioToSelect) {
        radioToSelect.isChecked.set(true);
        radioToSelect.detectChange();
        this.selectedRadio.set({
          id: radioToSelect.id(),
          value: radioToSelect.value()
        });
      } else {
        this.selectedRadio.set(null);
      }
    } else {
      const lastSelectedRadio = selectedRadios.pop();
      if (!lastSelectedRadio) return;
      this._unCheckAllRadios(selectedRadios);
      this.selectedRadio.set({
        id: lastSelectedRadio.id(),
        value: lastSelectedRadio.value()
      });
    }
    this._emitSelection();
  }

  private _unCheckAllRadios(radios: RadioButtonComponent[]): void {
    radios.forEach(radio => {
      radio.isChecked.set(false);
      radio.detectChange();
    });
  }

  private _emitSelection(): void {
    this.selectionChange.emit(this.selectedRadio());
  }
}
